import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Users as UsersIcon, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  MoreHorizontal,
  UserPlus,
  Filter,
  AlertTriangle,
  ToggleLeft,
  ToggleRight,
  Key
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { usePageTitle } from '../contexts/PageTitleContext';
import UserModal from '../components/UserModal';
import userService, { User as UserType } from '../services/userService';
import toast from 'react-hot-toast';
import { formatDateTime } from '../utils/dateUtils';

const Users: React.FC = () => {
  const { t } = useLanguage();
  usePageTitle(t('admin.users'), t('nav.settings'));
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);
  const [userModalMode, setUserModalMode] = useState<'create' | 'edit'>('create');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserType | null>(null);

  // Role display mapping for single-character codes
  const getRoleDisplay = (roleCode: string): string => {
    const roleMap: Record<string, string> = {
      'A': 'Admin',
      'S': 'Supervisor',
      'O': 'Operator',
      'M': 'Manager',
      'W': 'Warehouse',
      'R': 'Route'
    };
    return roleMap[roleCode] || roleCode;
  };

  // Fetch users from API
  const { data: usersResponse, isLoading, error } = useQuery(
    ['users', searchTerm, roleFilter], 
    () => userService.getUsers({ search: searchTerm, role: roleFilter !== 'all' ? roleFilter : undefined }),
    {
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: false
    }
  );

  // Extract users array from API response
  const users = usersResponse?.users || [];

  // Fetch stats from API (DB-wide, not just current page)
  const { data: statsResponse } = useQuery(
    ['users', 'stats'],
    () => userService.getUserStats(),
    { staleTime: 30000, refetchOnWindowFocus: false }
  );

  // Add debug logging
  React.useEffect(() => {
  }, [usersResponse, users, isLoading, error]);

  // Delete user mutation
  const deleteMutation = useMutation(
    (userId: string) => userService.deleteUser(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users']);
        toast.success(t('admin.userDeletedSuccess'));
        setShowDeleteModal(false);
        setUserToDelete(null);
      },
      onError: (error: any) => {
        toast.error(error.message || t('admin.failedToDeleteUser'));
      },
    }
  );

  // Toggle user status mutation
  const toggleStatusMutation = useMutation(
    (userId: string) => userService.toggleUserStatus(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users']);
        toast.success(t('admin.userStatusUpdated'));
      },
      onError: (error: any) => {
        toast.error(error.message || t('admin.failedToUpdateUserStatus'));
      },
    }
  );

  const filteredUsers = users.filter(user => {
    const fullName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim();
    const email = user.email || '';
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         fullName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'A': // Admin
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'S': // Supervisor
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200';
      case 'M': // Manager
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'O': // Operator
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'W': // Warehouse
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'R': // Route
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const formatLastLogin = (lastLogin?: string | null) => {
    if (!lastLogin) return 'Never';
    return formatDateTime(lastLogin);
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setUserModalMode('create');
    setShowUserModal(true);
  };

  const handleEditUser = (user: UserType) => {
    setEditingUser(user);
    setUserModalMode('edit');
    setShowUserModal(true);
  };

  const handleDeleteUser = (user: UserType) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleToggleStatus = (user: UserType) => {
    toggleStatusMutation.mutate(user.id);
  };

  const confirmDelete = () => {
    if (userToDelete) {
      deleteMutation.mutate(userToDelete.id);
    }
  };

  const handleCloseUserModal = () => {
    setShowUserModal(false);
    setEditingUser(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error state */}
      {!!error && (
        <div className="border border-red-200 bg-red-50 text-red-700 px-4 py-3 rounded" role="alert">
          <div className="font-medium">{t('admin.failedToLoadUsers')}</div>
          <div className="text-sm mt-1">{(error as any)?.response?.data?.message || (error as Error).message || t('admin.pleaseRefresh')}</div>
        </div>
      )}
      {/* Header */}
      <div className="flex justify-end items-center">
        <button 
          className="btn-primary flex items-center space-x-2"
          onClick={handleCreateUser}
        >
          <UserPlus className="w-4 h-4" />
          <span>{t('admin.addUser')}</span>
        </button>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('admin.searchUsers')}
                className="input-field pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-secondary-500" />
            <select
              className="input-field w-auto"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">{t('admin.allRoles')}</option>
              <option value="A">{t('admin.admin')}</option>
              <option value="S">{t('admin.supervisor')}</option>
              <option value="M">{t('admin.manager')}</option>
              <option value="O">{t('admin.operator')}</option>
              <option value="W">{t('admin.warehouse')}</option>
              <option value="R">{t('admin.route')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  {t('admin.user')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  {t('admin.role')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  {t('common.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  {t('admin.lastLogin')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-secondary-200 dark:divide-gray-700">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-secondary-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center">
                          <UsersIcon className="h-5 w-5 text-white" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-secondary-900 dark:text-white">
                          {user.fullName || 
                           (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username)
                          }
                        </div>
                        <div className="text-sm text-secondary-500 dark:text-secondary-400">{user.email || user.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                      {getRoleDisplay(user.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      !user.disabled ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {!user.disabled ? t('admin.active') : t('admin.inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                    {formatLastLogin(user.lastLogin || user.last_login)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button 
                        className="text-primary-600 hover:text-primary-900"
                        onClick={() => handleEditUser(user)}
                        title={t('admin.editUser')}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        className={`${!user.disabled ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}`}
                        onClick={() => handleToggleStatus(user)}
                        title={!user.disabled ? t('admin.deactivateUser') : t('admin.activateUser')}
                        disabled={toggleStatusMutation.isLoading}
                      >
                        {!user.disabled ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                      </button>
                      <button 
                        className="text-red-600 hover:text-red-900"
                        onClick={() => handleDeleteUser(user)}
                        title={t('admin.deleteUser')}
                        disabled={deleteMutation.isLoading}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button className="text-secondary-400 hover:text-secondary-600">
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <UsersIcon className="mx-auto h-12 w-12 text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">{t('admin.noUsersFound')}</h3>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              {searchTerm ? t('admin.tryAdjustingSearch') : t('admin.getStartedCreatingUser')}
            </p>
          </div>
        )}
      </div>

      {/* Users Stats (DB-wide via /users/stats/summary) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <UsersIcon className="w-4 h-4 text-white" />
              </div>
            </div>
            <div className="ml-3">
              <div className="text-sm font-medium text-secondary-500 dark:text-secondary-400">{t('admin.totalUsers')}</div>
              <div className="text-lg font-semibold text-secondary-900 dark:text-white">
                {statsResponse ? statsResponse.roleDistribution.reduce((sum: number, r: any) => sum + Number(r.total || 0), 0) : users.length}
              </div>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                <UsersIcon className="w-4 h-4 text-white" />
              </div>
            </div>
            <div className="ml-3">
              <div className="text-sm font-medium text-secondary-500 dark:text-secondary-400">{t('admin.activeUsers')}</div>
              <div className="text-lg font-semibold text-secondary-900 dark:text-white">
                {statsResponse ? statsResponse.roleDistribution.reduce((sum: number, r: any) => sum + Number(r.active || 0), 0) : users.filter(u => !u.disabled).length}
              </div>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                <UsersIcon className="w-4 h-4 text-white" />
              </div>
            </div>
            <div className="ml-3">
              <div className="text-sm font-medium text-secondary-500 dark:text-secondary-400">{t('admin.admins')}</div>
              <div className="text-lg font-semibold text-secondary-900 dark:text-white">
                {statsResponse ? (statsResponse.roleDistribution.find((r: any) => r.role === 'admin')?.total ?? 0) : users.filter(u => u.role === 'admin').length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Modal */}
      <UserModal
        isOpen={showUserModal}
        onClose={handleCloseUserModal}
        user={editingUser}
        mode={userModalMode}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-secondary-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-secondary-900 dark:text-white">{t('admin.deleteUser')}</h3>
                </div>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  {t('admin.confirmDeleteUser')} "{userToDelete.firstName} {userToDelete.lastName}" 
                  ({userToDelete.username})? {t('admin.actionCannotBeUndone')}
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  className="px-4 py-2 text-sm font-medium text-secondary-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-secondary-300 dark:border-gray-600 rounded-md hover:bg-secondary-50 dark:hover:bg-gray-600"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteMutation.isLoading}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  onClick={confirmDelete}
                  disabled={deleteMutation.isLoading}
                >
                  {deleteMutation.isLoading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  )}
                  {t('admin.deleteUser')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;